import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Calendar, Users } from 'lucide-react';
import { contactInfo } from '../data/mockData';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    guests: '2',
    roomType: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Prepare data with correct types
      const submissionData = {
        name: formData.name,
        phone: formData.phone,
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
        guests: parseInt(formData.guests),
        roomType: formData.roomType || null,
        message: formData.message || null
      };
      
      // Only include email if provided
      if (formData.email && formData.email.trim() !== '') {
        submissionData.email = formData.email;
      }
      
      const response = await axios.post(`${BACKEND_URL}/api/bookings/inquiry`, submissionData);
      
      if (response.status === 200 || response.status === 201) {
        toast.success('Booking inquiry sent successfully! We will contact you soon.', {
          duration: 4000
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          checkIn: '',
          checkOut: '',
          guests: '2',
          roomType: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting booking inquiry:', error);
      console.error('Error details:', error.response?.data);
      const errorMessage = error.response?.data?.detail || 'Failed to send inquiry. Please try calling or WhatsApp instead.';
      toast.error(errorMessage, {
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Contact & Book Hotel Room in Varanasi — Ved Homestays"
        description="Book your room at Ved Homestays near Kashi Vishwanath Temple, Varanasi. Call +91 9984397001, WhatsApp, or fill our booking form. Best rates guaranteed for hotel rooms near Ganga ghats."
        keywords="book hotel varanasi, contact ved homestays, varanasi hotel booking, homestay near kashi vishwanath temple booking, varanasi hotel phone number, book room near ganga ghat, varanasi accommodation booking, hotel reservation varanasi, whatsapp hotel booking varanasi"
        url="https://www.vedhomestays.com/contact"
        breadcrumbs={[
          { name: "Home", url: "https://www.vedhomestays.com/" },
          { name: "Contact & Book", url: "https://www.vedhomestays.com/contact" }
        ]}
      />
      
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_banarasi-living/artifacts/rx68f05m_kashi%20vishwanath%20updated.jpg"
            alt="Kashi Vishwanath Temple at Night, Varanasi"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/60"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 drop-shadow-lg">Book Hotel in Varanasi</h1>
          <p className="text-xl md:text-2xl text-orange-300 drop-shadow-md">Ved Homestays — Steps from Kashi Vishwanath Temple</p>
          <p className="text-lg mt-2 drop-shadow-md">We're here to help plan your Varanasi stay</p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-b from-white to-orange-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-6">Get In Touch</h2>
                <p className="text-gray-600 mb-8 text-lg">
                  Have questions about our rooms, pricing, or availability? We're happy to help! 
                  Reach out to us through any of the following channels.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="text-orange-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1">Phone</h3>
                      <a href={`tel:${contactInfo.phone}`} className="text-gray-600 hover:text-orange-600 block">
                        {contactInfo.phone}
                      </a>
                      <a href={`tel:${contactInfo.phoneSecondary}`} className="text-gray-600 hover:text-orange-600 block mt-1">
                        {contactInfo.phoneSecondary}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="text-orange-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1">Email</h3>
                      <a href={`mailto:${contactInfo.email}`} className="text-gray-600 hover:text-orange-600 break-all">
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-orange-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1">Address</h3>
                      <p className="text-gray-600">
                        {contactInfo.address}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick Info */}
                <div className="mt-8 p-6 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl">
                  <h3 className="font-semibold text-gray-800 mb-3">Quick Information</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• 5 minute walk to Kashi Vishwanath Temple</li>
                    <li>• Steps away from Ganga Aarti Ghats (5 minutes)</li>
                    <li>• 24/7 availability for check-in assistance</li>
                  </ul>
                </div>

                {/* Varanasi Tours */}
                <div className="mt-6 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 text-lg">Varanasi Tour Arrangements</h3>
                      <p className="text-sm text-gray-600 mt-1">We can arrange guided tours of Varanasi for you</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 ml-13">
                    <li>✓ Ganga Aarti evening ceremony tour</li>
                    <li>✓ Temple tours (Kashi Vishwanath, Sankat Mochan, etc.)</li>
                    <li>✓ Old city walking tours</li>
                    <li>✓ Sarnath Buddhist site visit</li>
                    <li>✓ Local food & culture experiences</li>
                  </ul>
                  <p className="text-xs text-gray-600 mt-4 italic">
                    Ask us about customized tour packages when you book your stay
                  </p>
                </div>
              </div>

              {/* Booking Inquiry Form */}
              <div>
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-3xl font-bold text-gray-800 mb-2">Booking Inquiry</h2>
                  <p className="text-gray-600 mb-6">Fill out the form and we'll get back to you shortly</p>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Your name"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="your@email.com (optional)"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="+91 XXXXX XXXXX"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <Calendar size={16} className="inline mr-1" />
                          Check-in Date *
                        </label>
                        <input
                          type="date"
                          name="checkIn"
                          value={formData.checkIn}
                          onChange={handleChange}
                          required
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <Calendar size={16} className="inline mr-1" />
                          Check-out Date *
                        </label>
                        <input
                          type="date"
                          name="checkOut"
                          value={formData.checkOut}
                          onChange={handleChange}
                          required
                          min={formData.checkIn || new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <Users size={16} className="inline mr-1" />
                          Number of Guests *
                        </label>
                        <select
                          name="guests"
                          value={formData.guests}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                            <option key={num} value={num}>{num} {num === 1 ? 'Guest' : 'Guests'}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Room Type Preference
                        </label>
                        <select
                          name="roomType"
                          value={formData.roomType}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                          <option value="">Any Available</option>
                          <option value="deluxe">Deluxe Room</option>
                          <option value="premium">Standard Room</option>
                          <option value="ultra-large">Ultra-Large Room</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Additional Message
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="4"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                        placeholder="Any special requirements or questions?"
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      className="w-full px-6 py-4 bg-orange-600 text-white rounded-lg font-semibold text-lg hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2"
                    >
                      <Send size={20} />
                      <span>Send Booking Inquiry</span>
                    </button>

                    <p className="text-xs text-gray-500 text-center">
                      By submitting this form, you agree to be contacted regarding your booking inquiry
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Map Section */}
          <div className="max-w-6xl mx-auto mt-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Find Us Here</h2>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <iframe
                src={contactInfo.mapUrl}
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
